<template>
  <div class="login-block">

    <div class="block">
      <v-row class="unit">
        <v-col cols="12" sm="6">
          <h2 class="mb-0">{{ $t('auth.logInAccount') }}</h2>
        </v-col>
        <v-col cols="12" sm="6">
          <div class="login">
            <span>{{ $t('auth.logInAsk') }}</span>
            <a class="link-b" @click="goToLogOut()" data-attr="sign-up">{{ $t('auth.signUp') }}</a>
          </div>
        </v-col>
      </v-row>

      <v-form ref="login">
        <v-row class="unit" align="start">
          <v-col cols="12" md="6" data-attr="login-email">
            <v-text-field
                :label="$t('auth.email')"
                v-model.trim="auth.email"
                outlined
                @keyup.enter="submit"
                :rules="rulesEmail"
                validate-on-blur
            />
          </v-col>

          <v-col cols="12" md="6" data-attr="password">
            <v-text-field
                :label="$t('auth.password')"
                v-model.trim="auth.password"
                outlined
                :rules="rulesPassword"
                :type="showPassword ? 'text' : 'password'"
                :append-icon="showPassword ? 'visibility' : 'visibility_off'"
                @click:append="showPassword = !showPassword"
                @keyup.enter="submit"
            />
          </v-col>

          <v-expand-transition>
            <template v-if="authError">
              <div class="info-message invalid py-0 px-3" data-attr="auth-invalid">
                <v-icon class="sr-icon-close"/>
                <span class="error-message" v-html="$t('validation.'+authError)"></span>
              </div>
            </template>
          </v-expand-transition>
        </v-row>

        <v-row :class="[flow === $options.FLOW.TRIAL ? ['justify-end', 'fit-end'] : '', 'unit']">
          <v-col class="submit" cols="auto" v-if="flow !== $options.FLOW.TRIAL">
            <v-btn
                color="primary"
                dark
                @click="submit()"
                data-attr="signin-btn"
            >
              {{ $t('auth.signIn') }}
            </v-btn>
          </v-col>

          <v-col cols="auto">
            <a
                :href="$options.URLS.RESET_PASSWORD"
                target="_blank"
                class="link-b"
                data-attr="reset-password">{{ $t('auth.forgotPassword') }}</a>
          </v-col>
        </v-row>
      </v-form>

      <verification
          v-model="verification"
          ref="verification"
          v-if="flow === $options.FLOW.TRIAL && !isVirtualHostingV2"
          :flow="flow"
          :parentClass="'.login-block'"
      />
    </div>

    <popup-loader :show-popup="showPopupLoader" :title="$t('auth.authorizeText')"/>
  </div>
</template>

<script>
  import PopupLoader from '@/shared/components/Loaders/PopupLoader.vue';
  import { SETTINGS_GETTERS, SETTINGS_MUTATIONS } from "@/store/modules/settings";
  import { AUTH_MUTATIONS } from "@/store/modules/auth";
  import { login } from "@/store/api/auth";
  import { AUTH_ERROR, COUNTRIES, FLOW, URLS } from '@/constants';
  import * as Sentry from "@sentry/vue";
  import Verification from "./Verification.vue";

  export default {
    name: "Login",
    FLOW,
    URLS,
    components: {
      PopupLoader,
      Verification,
    },
    props: {
      flow: { type: String },
      isVirtualHostingV2: { type: Boolean, default: false },
    },
    data: () => ({
      loading: false,
      auth: {
        email: '',
        password: '',
      },
      verification: {
        country: 'UA',
        phonenumber: '',
        verifytype: 'sms'
      },
      showPassword: false,
      receiveNewsletter: true,
      showPopupLoader: false,
      authError: undefined,
    }),
    computed: {
      rulesEmail() {
        const rules = this.$attrs.rules || [];
        rules.push(v => v && !!v.trim() || this.$t('validation.requiredEmail'));

        return rules;
      },
      rulesPassword() {
        const rules = this.$attrs.rules || [];
        rules.push(v => v && !!v.trim() || this.$t('validation.required'));

        return rules;
      },
    },
    methods: {
      goToLogOut() {
        this.$emit('getLogOut', true);
      },
      isFormValidate() {
        let validate =  this.$refs.login.validate();
        if(this.flow === FLOW.TRIAL && !this.isVirtualHostingV2) {
          const verificationValidate = this.$refs.verification.validate();
          validate = this.$refs.login.validate() && verificationValidate;
        }
        return validate;
      },
      async submit() {
        const valid = this.isFormValidate();
        if (!valid) {
          this.$nextTick(() => {
            const el = this.$el.querySelector(".v-messages.error--text:first-of-type");
            if(el) { this.$vuetify.goTo(el, { offset: 80 }) };
          });
        }

        if(!valid) return

        this.loading = true;
        this.showPopupLoader = true;
        this.authError = undefined;

        const WHMCS = document.cookie && (document.cookie.split(";").find(cook => cook.match(/WHMCS[a-zA-Z0-9]{12}=/)) || '').split("=")[0] || '';
        document.cookie = `${WHMCS}=;max-age=31536000;path=/`;

        try {
          const result = await login(this.auth);

          const now = new Date();
          const expireTime = now.setFullYear(now.getFullYear() + 1);
          now.setTime(expireTime);
          document.cookie = `WHMCSUser=${result.authcookie};expires=${now.toUTCString()};max-age=31536000;path=/`;

          const setAuth = {
            cookie: result.authcookie,
            auth: result.client
          }
          this.$store.commit(`settings/${SETTINGS_MUTATIONS.SET_AUTH}`, setAuth);
          this.$store.commit(`settings/${SETTINGS_MUTATIONS.SET_TRIAL_AUTH}`, true);
          this.gtmEvent(result.client.id);

          this.goToLogOut();
        } catch (errors) {
          Sentry.captureException(errors);
          this.authError = AUTH_ERROR.MESSAGE[errors?.data?.message?.[0]];
        }  finally {
          this.loading = false;
          this.showPopupLoader = false;
        }
      },
      gtmEvent(userId) {
        window.dataLayer.push({
          event: 'login',
          userId
        });
      }
    },
    watch: {
      auth: {
        handler() {
          this.authError = false;
        },
        deep: true,
      },
      verification: {
        handler(val) {
          this.$store.commit(`auth/${AUTH_MUTATIONS.SET_VERIFICATION}`, val);
        },
        deep: true,
      },
    }
  }
</script>

<style lang="scss">
@media (min-width: $desktop) {
  .unit.justify-end.fit-end {
    width: fit-content;
    margin-left: auto;
  }
}


</style>
